import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { id: "seed",   amount: 5,  label: "A Seed",    sub: "$5"  },
  { id: "branch", amount: 15, label: "A Branch",  sub: "$15" },
  { id: "vine",   amount: 50, label: "A Vine",    sub: "$50" },
];

export default function Donate({ packetId }) {
  const [selected, setSelected] = useState(null);     // package_id
  const [customAmount, setCustomAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [thankYou, setThankYou] = useState(null);     // {amount} once paid

  // Detect return from Stripe (?donation_session_id=…) and poll for status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("donation_session_id");
    const canceled = params.get("donation_canceled");
    if (canceled) {
      // Strip the param so a refresh doesn't loop the message
      const url = window.location.pathname;
      window.history.replaceState({}, "", url);
      return;
    }
    if (!sid) return;

    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/donate/status/${sid}`);
        if (data.payment_status === "paid") {
          setThankYou({ amount: data.amount });
          // Strip the session param so refresh doesn't re-trigger
          const url = window.location.pathname;
          window.history.replaceState({}, "", url);
          return;
        }
        if (data.status === "expired" || data.status === "complete") {
          // complete but unpaid — surface canceled-style state
          const url = window.location.pathname;
          window.history.replaceState({}, "", url);
          return;
        }
        if (attempts < 8) {
          attempts += 1;
          setTimeout(poll, 2000);
        }
      } catch {
        /* swallow — user can re-attempt */
      }
    };
    poll();
  }, []);

  const initiate = async () => {
    setError("");
    let body = { origin_url: window.location.origin, packet_id: packetId };
    if (selected === "custom") {
      const n = parseFloat(customAmount);
      if (!n || n < 1) {
        setError("Enter an amount of $1 or more.");
        return;
      }
      body.custom_amount = Math.round(n * 100) / 100;
    } else if (selected) {
      body.package_id = selected;
    } else {
      setError("Choose an amount first.");
      return;
    }

    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/donate/create-session`, body);
      if (!data?.url) throw new Error("No checkout URL returned.");
      window.location.href = data.url;
    } catch (e) {
      setBusy(false);
      setError(e?.response?.data?.detail || "Could not open checkout. Please try again.");
    }
  };

  if (thankYou) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="mt-24 max-w-2xl mx-auto text-center px-6"
        data-testid="donate-thanks"
      >
        <div className="overline mb-4">Received with Gratitude</div>
        <h3 className="font-serif-display italic text-3xl sm:text-4xl text-[#F0E6D2] mb-6">
          Thank you. Your gift was received.
        </h3>
        <p className="text-[#F0E6D2] font-body font-light text-base sm:text-lg leading-relaxed">
          Every gift quietly carries the next packet to someone who cannot pay for theirs.
          May the Lord bless what you have sown into this work.
        </p>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9 }}
      className="mt-24 max-w-2xl mx-auto px-6"
      data-testid="donate-card"
      data-readaloud-skip="true"
    >
      <div className="border border-[#FFE7A0]/20 bg-[#050818]/40 backdrop-blur-sm p-8 sm:p-10">
        <div className="overline mb-4 text-center">An Offering, If The Spirit Leads</div>
        <h3 className="font-serif-display italic text-2xl sm:text-3xl text-[#F0E6D2] mb-6 text-center leading-tight">
          If this packet has met you in your walk with Christ,
          <br />
          consider sowing into the next one.
        </h3>
        <p className="text-[#F0E6D2] font-body font-light text-base sm:text-lg leading-relaxed text-center mb-10">
          The Matrix is offered freely — no account, no fee, no condition. But each packet quietly carries
          a real cost to the developer who tends this work. If you have been freed, comforted, or
          strengthened here, your gift keeps the doors open for the next person who cannot pay for theirs.
          Any amount, in any season. There is no obligation.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { setSelected(p.id); setCustomAmount(""); setError(""); }}
              disabled={busy}
              className={`p-4 border transition-all duration-500 ${
                selected === p.id
                  ? "border-[#FFE7A0] bg-[#FFE7A0]/5"
                  : "border-[#FFE7A0]/15 hover:border-[#FFE7A0]/40"
              } ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid={`donate-preset-${p.id}-btn`}
            >
              <div className="font-cinzel text-xl sm:text-2xl tracking-[0.18em] uppercase text-[#FFE7A0]">{p.sub}</div>
              <div className="font-serif-display italic text-lg text-[#F0E6D2] mt-1">{p.label}</div>
            </button>
          ))}
          <button
            type="button"
            onClick={() => { setSelected("custom"); setError(""); }}
            disabled={busy}
            className={`p-4 border transition-all duration-500 ${
              selected === "custom"
                ? "border-[#FFE7A0] bg-[#FFE7A0]/5"
                : "border-[#FFE7A0]/15 hover:border-[#FFE7A0]/40"
            } ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="donate-preset-custom-btn"
          >
            <div className="font-cinzel text-xl sm:text-2xl tracking-[0.18em] uppercase text-[#FFE7A0]">$ Other</div>
            <div className="font-serif-display italic text-lg text-[#F0E6D2] mt-1">Your Gift</div>
          </button>
        </div>

        {selected === "custom" && (
          <div className="mb-5" data-testid="donate-custom-input-wrap">
            <label className="overline block mb-2 text-[0.6rem]" htmlFor="donate-custom-amount">
              Amount in USD
            </label>
            <input
              id="donate-custom-amount"
              type="number"
              min="1"
              step="0.01"
              inputMode="decimal"
              placeholder="25.00"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              disabled={busy}
              className="rm-input"
              data-testid="donate-custom-amount-input"
            />
          </div>
        )}

        {error && (
          <p className="text-sm text-[#FFE7A0]/85 italic mb-4 text-center" data-testid="donate-error">
            {error}
          </p>
        )}

        <div className="flex justify-center">
          <button
            type="button"
            onClick={initiate}
            disabled={busy || !selected}
            className="rm-btn"
            data-testid="donate-submit-btn"
          >
            {busy ? "Opening checkout…" : "Continue to secure checkout"}
          </button>
        </div>

        <p className="mt-6 text-center font-cinzel text-[0.7rem] uppercase tracking-[0.25em] text-[#F0E6D2]">
          Processed securely by Stripe
        </p>
      </div>
    </motion.section>
  );
}
